// The mobile app shows workout instructions over the workout header image and
// cuts them off with "..." after 6 lines (see ScrollWithAnimatedHeader.js in
// the trybe-mobile repo). Measured on the narrowest phones in common use
// (360dp-wide Androids), about 200 characters of text fit in those 6 lines.
//
// Both limits are enforced here as one character budget: 6 line slots of 33
// characters each, 198 characters total (33 is the fewest characters a full
// line holds on a small phone - all-caps text). Typing costs 1 per character.
// A line break rounds the current line's cost up to the next whole slot,
// because a broken line takes up a full line on the phone no matter how short
// its text is. Making the budget exactly 6 x 33 means "within budget" and
// "within 6 lines" are the same check. Text that stays inside the budget can
// never be cut off in the app.

export const MOBILE_INSTR_MAX_LINES = 6
const CHARS_PER_LINE = 33
export const MOBILE_INSTR_MAX_CHARS = MOBILE_INSTR_MAX_LINES * CHARS_PER_LINE

// Cost of the text in budget characters. Every hard line except the one still
// being typed is rounded up to a whole number of line slots; an empty line
// costs a full slot.
export const instructionsBudgetUsed = (text) => {
  if (!text) return 0
  const hardLines = text.split('\n')
  const last = hardLines.pop()
  const brokenLinesCost = hardLines.reduce(
    (sum, line) => sum + Math.max(1, Math.ceil(line.length / CHARS_PER_LINE)) * CHARS_PER_LINE,
    0
  )
  return brokenLinesCost + last.length
}

// How many of the phone's 6 lines the text occupies. Drives the "x/6 lines"
// counter next to the description box.
export const instructionsLinesUsed = (text) => Math.ceil(instructionsBudgetUsed(text) / CHARS_PER_LINE)

export const fitsOnMobile = (text) => instructionsBudgetUsed(text) <= MOBILE_INSTR_MAX_CHARS

// Longest prefix of the text that still fits the budget. Used when a paste
// (or a keystroke at the limit) would overflow - we keep what fits instead of
// rejecting the whole input.
export const clampToFit = (text) => {
  if (fitsOnMobile(text)) return text
  let lo = 0
  let hi = text.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (fitsOnMobile(text.slice(0, mid))) {
      lo = mid
    } else {
      hi = mid - 1
    }
  }
  // Don't cut an emoji (or any 2-unit character) in half at the boundary -
  // that would leave a broken replacement character at the end.
  if (lo > 0 && /[\uD800-\uDBFF]/.test(text[lo - 1])) {
    lo -= 1
  }
  return text.slice(0, lo)
}
